import {
  AuthenticationTemplate,
  PhoneForm,
  AnimatedLayout,
  useAnimationLayout,
  OTPForm,
} from '@/components';
import { useRouter } from 'next/router';
import { useAuth } from '@/components/providers/preauth/AuthProvider';
import { URLS } from '@/utils/constants';
import { ConfirmationResult, RecaptchaVerifier } from 'firebase/auth';
import { useState, useEffect } from 'react';
import { auth } from '@/utils/firebase';
import { logError, logInfo, logDebug } from '@/utils/logger';

export enum Stage {
  PHONE,
  OTP,
}

export default function Phone() {
  const router = useRouter();
  const authProvider = useAuth();

  const [stage, setStage] = useState(Stage.PHONE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [recaptchaLoading, setRecaptchaLoading] = useState(true);

  const { setBack } = useAnimationLayout();

  const handleConfirm = async (_phoneNumber: string) => {
    try {
      const appVerifier = (window as any).recaptchaVerifier;
      const confirmationResult: ConfirmationResult =
        await authProvider.signInWithPhoneNumber(_phoneNumber, appVerifier);
      (window as any).confirmationResult = confirmationResult;
      setPhoneNumber(_phoneNumber);
      setStage(Stage.OTP);
      return confirmationResult;
    } catch (error) {
      logError(error, 'handleConfirm');
    }
  };

  const getMaskedPhoneNumber = () => {
    const first2 = phoneNumber.substring(0, 2);
    const last2 = phoneNumber.substring(phoneNumber.length - 2);
    const mask = phoneNumber
      .substring(2, phoneNumber.length - 2)
      .replace(/\d/g, 'x');
    const maskedPhoneNumber = first2 + mask + last2;
    return maskedPhoneNumber;
  };

  const handleConfirmCode = async (otpCode: string, cbError: () => void) => {
    try {
      const res = await authProvider.confirmOTPCode(
        otpCode,
        (window as any).confirmationResult,
      );
      router.replace(URLS.home);
    } catch (error) {
      logError(error, 'handleConfirmCode');
      cbError();
    }
  };

  const onResendOTP = () => {
    handleConfirm(phoneNumber);
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && stage === Stage.PHONE) {
      const verifier = new RecaptchaVerifier(
        'recaptcha-container',
        {
          size: 'invisible',
          callback: (response: any) => {
            logDebug('Solved!', 'RecaptchaVerifier callback');
          },
        },
        auth,
      );

      verifier
        .render()
        .then((widgetId) => {
          (window as any).recaptchaVerifier = verifier;
          (window as any).recaptchaWidgetId = widgetId;
          setRecaptchaLoading(false);
          logInfo('Recaptcha rendered successfully', 'useEffect', { widgetId });
        })
        .catch((error) => {
          logError(error, 'Recaptcha render');
        });
    }
  }, [stage]);

  return (
    <>
      {stage === Stage.OTP && (
        <AnimatedLayout>
          <OTPForm
            onBackClick={() => {
              setBack(true);
              setStage(Stage.PHONE);
            }}
            maskedPhoneNumber={getMaskedPhoneNumber()}
            onConfirmCode={handleConfirmCode}
            onResendOTP={onResendOTP}
          />
        </AnimatedLayout>
      )}
      {stage === Stage.PHONE && (
        <AnimatedLayout>
          <PhoneForm
            onBackClick={() => {
              setBack(true);
              router.replace(URLS.auth);
            }}
            onConfirm={handleConfirm}
            stage={stage}
            setStage={setStage}
            recaptchaLoading={recaptchaLoading}
          />
        </AnimatedLayout>
      )}
    </>
  );
}

Phone.getLayout = function PageLayout(page: React.ReactNode) {
  return <AuthenticationTemplate>{page}</AuthenticationTemplate>;
};
